import type { ComplianceIssue } from './types.js';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Regulations treated as "internal" — not a regulatory breach, just a hint
 * that the content is borderline AI-like. We keep the row (so merchants see
 * there's *something* odd) but drop it from the Regulation column so the
 * column answers "which law am I breaking" rather than "what internal signal
 * did the scanner emit".
 */
export const INTERNAL_REGULATIONS = new Set<string>(['BEST_PRACTICE_BORDERLINE']);

const I18N_KEY_BY_ID: Record<string, string> = {
  EU_AI_ACT_50_2: 'regulations.euArt50',
  EU_AI_ACT_50_IMG: 'regulations.euArt50Img',
  FTC_AI_DISCLOSURE: 'regulations.ftc',
  NY_SYNTHETIC_PERFORMER: 'regulations.ny',
  BEST_PRACTICE_BORDERLINE: 'regulations.bestPractice',
};

export function regulationLabel(id: string, t: TFn): string {
  const key = I18N_KEY_BY_ID[id];
  if (!key) return id;
  const v = t(key);
  return v === key ? id : v;
}

export function publicRegulationIds(ids: string[]): string[] {
  return ids.filter((id) => !INTERNAL_REGULATIONS.has(id));
}

/**
 * Short jurisdiction-style label for compact contexts (activity log, badges).
 * Collapses the two EU sub-rules to a single "EU" so we don't repeat ourselves
 * when a product trips both. Drops internal signals.
 */
const SHORT_BY_ID: Record<string, string> = {
  EU_AI_ACT_50_2: 'EU',
  EU_AI_ACT_50_IMG: 'EU',
  FTC_AI_DISCLOSURE: 'FTC',
  NY_SYNTHETIC_PERFORMER: 'NY',
};

export function formatRegulationsShort(ids: string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of publicRegulationIds(ids)) {
    const s = SHORT_BY_ID[id] ?? id;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out.join(', ');
}

export function formatPublicRegulations(
  ids: string[],
  t: TFn,
  emptyFallback = '—',
): string {
  const filtered = publicRegulationIds(ids);
  if (filtered.length === 0) return emptyFallback;
  return filtered.map((id) => regulationLabel(id, t)).join(', ');
}

/**
 * Maps AI score (0-100) → human "band" label. We deliberately don't expose the
 * thresholds as config: they match the scanner's internal severity boundaries
 * (see regulations.json minAiScore values — 45/60/70/80).
 */
export function riskBandKey(score: number): 'high' | 'medium' | 'low' {
  if (score >= 70) return 'high';
  if (score >= 45) return 'medium';
  return 'low';
}

export function contentTypeLabel(ct: ComplianceIssue['contentType'], t: TFn): string {
  // Client type allows 'description' | 'meta_description' | 'image' |
  // 'image_alt' | 'title'; the DB schema collapses meta* and image* into
  // 'meta' / 'image'. Normalise both shapes to the same i18n key.
  const normalised = ct === 'meta_description' ? 'meta' : ct === 'image_alt' ? 'image' : ct;
  const key = `dashboard.contentType.${normalised}`;
  const v = t(key);
  return v === key ? normalised : v;
}
